import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import './Analytics.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [timeframe, setTimeframe] = useState('month');
  const { token, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnalytics();
  }, [timeframe]);

  const fetchAnalytics = async () => {
    try {
      const role = user?.role === 'owner' ? 'owner' : 'vendor';
      const res = await fetch(`${API_BASE}/analytics/${role}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await res.json();
      if (result.success) {
        setData(result.data);
      } else {
        setMockData();
      }
    } catch (error) {
      console.error('Analytics error:', error);
      setMockData();
    } finally {
      setLoading(false);
    }
  };

  const setMockData = () => {
    setData({
      totalSpent: 284500,
      avgPerJob: 15800,
      jobsCompleted: 18,
      activeJobs: 3,
      topServices: [
        { name: 'GMDSS', count: 8, revenue: 124000, percentage: 44 },
        { name: 'Navigation', count: 6, revenue: 89000, percentage: 31 },
        { name: 'Safety', count: 4, revenue: 71500, percentage: 25 }
      ],
      monthlyData: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        values: [18500, 22400, 19800, 25600, 23400, 27800]
      },
      recentActivity: [
        { id: 1, action: 'Job awarded', entity: 'MV Ocean Star', amount: 12500, date: '2024-03-15' },
        { id: 2, action: 'Payment released', entity: 'LNG Navigator', amount: 8500, date: '2024-03-14' }
      ]
    });
  };

  if (loading) return <div className="loading-spinner">Loading analytics...</div>;

  const maxValue = data?.monthlyData?.values ? Math.max(...data.monthlyData.values) : 100000;

  return (
    <div className="analytics-page">
      <div className="page-header">
        <h1><i className="fas fa-chart-line"></i> Analytics Dashboard</h1>
        <p>Track your spending, performance, and key metrics</p>
        <div className="header-actions">
          <select className="timeframe-select" value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last 90 Days</option>
            <option value="year">Last 12 Months</option>
          </select>
          <Button variant="outline" icon="fa-download">Export Report</Button>
          <Button variant="secondary" onClick={() => navigate(`/${user?.role}/dashboard`)} icon="fa-arrow-left">Back to Dashboard</Button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-dollar-sign"></i></div>
          <div className="stat-info">
            <div className="stat-value">${(data.totalSpent || data.totalEarnings || 0).toLocaleString()}</div>
            <div className="stat-label">{user?.role === 'owner' ? 'Total Spent' : 'Total Earnings'}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-chart-line"></i></div>
          <div className="stat-info">
            <div className="stat-value">${(data.avgPerJob || data.avgJobValue || 0).toLocaleString()}</div>
            <div className="stat-label">Avg. per Job</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-check-circle"></i></div>
          <div className="stat-info">
            <div className="stat-value">{data.jobsCompleted || 0}</div>
            <div className="stat-label">Jobs Completed</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-briefcase"></i></div>
          <div className="stat-info">
            <div className="stat-value">{data.activeJobs || 0}</div>
            <div className="stat-label">Active Jobs</div>
          </div>
        </div>
      </div>

      <div className="chart-card">
        <div className="chart-header">
          <h3>Monthly {user?.role === 'owner' ? 'Spending' : 'Earnings'} Trend</h3>
          <div className="chart-legend">
            <span><span className="legend-dot"></span> USD</span>
          </div>
        </div>
        <div className="bar-chart-container">
          <div className="bar-chart">
            {data.monthlyData.labels.map((label, idx) => {
              const height = (data.monthlyData.values[idx] / maxValue) * 200;
              return (
                <div key={idx} className="bar-item">
                  <div className="bar-wrapper">
                    <div className="bar" style={{ height: `${height}px` }}>
                      <span className="bar-value">${(data.monthlyData.values[idx] / 1000).toFixed(0)}k</span>
                    </div>
                  </div>
                  <span className="bar-label">{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="analytics-grid">
        <div className="top-services-card">
          <h3><i className="fas fa-chart-pie"></i> Top Services</h3>
          {data.topServices?.map((service, idx) => (
            <div key={idx} className="service-item">
              <div className="service-header">
                <span className="service-name">{service.name}</span>
                <span className="service-stats">{service.count} jobs • ${service.revenue.toLocaleString()}</span>
              </div>
              <div className="service-progress">
                <div className="progress-track">
                  <div className="progress-fill" style={{ width: `${service.percentage || (service.revenue / data.totalSpent * 100)}%` }}></div>
                </div>
                <span className="service-percent">{Math.round(service.percentage || (service.revenue / data.totalSpent * 100))}%</span>
              </div>
            </div>
          ))}
        </div>

        <div className="recent-activity-card">
          <h3><i className="fas fa-history"></i> Recent Activity</h3>
          <div className="activity-list">
            {data.recentActivity?.map(activity => (
              <div key={activity.id} className="activity-item">
                <div className="activity-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="activity-details">
                  <div className="activity-action">{activity.action}</div>
                  <div className="activity-entity">{activity.entity}</div>
                  <div className="activity-date">{new Date(activity.date).toLocaleDateString()}</div>
                </div>
                <div className="activity-amount">${activity.amount.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;